import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Skeleton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import usePackagingCheckApi, { getApiErrorMessage } from "../../components/PackagingCheck/usePackagingCheckApi";
import ConclusionBanner from "../../components/PackagingCheck/report/ConclusionBanner";
import useReportDownload, { reportFileName } from "../../components/PackagingCheck/report/useReportDownload";
import { downloadElementAsPdf } from "../../components/PackagingCheck/report/pdfExport";
import SectionComparison, { CHANGE_LABELS } from "../../components/PackagingCheck/results/SectionComparison";
import { StatusPill } from "../../components/PackagingCheck/results/CorrectionsList";
import {
  BORDER,
  BRAND,
  CORRECTION_STATUSES,
  CORRECTION_STATUS_ORDER,
  SEVERITIES,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  formatDateTime,
} from "../../components/PackagingCheck/constants";
import { checkTitle } from "../../components/PackagingCheck/RecentChecks";

const ALL_SEVERITIES = new Set(SEVERITIES.map((s) => s.key));
// Long corrections (a full ingredient list, a full warnings text) are cut
// in the report to keep it focused (spec 2.1); the Excel file has them in full
const MAX_CORRECTION_LENGTH = 220;
const statusRank = (result) => (result ? CORRECTION_STATUS_ORDER.indexOf(result.status) : CORRECTION_STATUS_ORDER.length);
const shorten = (text) => (text.length > MAX_CORRECTION_LENGTH ? `${text.slice(0, MAX_CORRECTION_LENGTH).trim()}…` : text);
const CHECK_LABELS = {
  duplicateWords: "כפל מילים",
  footnotes: "כוכביות והערות שוליים",
  numbers: "נתונים מספריים",
  parallelCorrections: "תיקון שבוצע רק בחלק מהאריזה",
  claimConsistency: "עקביות טענות",
  inciFormat: "תקינות INCI",
  fixedFields: "שדות קבועים וברקוד",
  fontSize: "גודל כתב",
  outlinedText: "טקסט שהומר לעקומות",
  mandatoryItems: "פריטי חובה",
};

const ReportSection = ({ number, title, children }) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5, pb: 1, borderBottom: `2px solid ${TEXT_PRIMARY}` }}>
      <Typography sx={{ fontWeight: 800, color: BRAND, fontSize: 15 }}>{number}.</Typography>
      <Typography sx={{ fontWeight: 800, color: TEXT_PRIMARY, fontSize: 17 }}>{title}</Typography>
    </Box>
    {children}
  </Box>
);

const Empty = ({ children }) => (
  <Typography sx={{ color: TEXT_SECONDARY, fontSize: 14, fontStyle: "italic" }}>{children}</Typography>
);

const cellSx = { fontSize: 13, verticalAlign: "top", borderBottom: `1px solid ${BORDER}`, py: 1 };
const headSx = { ...cellSx, fontWeight: 700, color: TEXT_SECONDARY, bgcolor: "#f8fafc", whiteSpace: "nowrap" };

const SimpleTable = ({ columns, rows }) => (
  <Table size="small" sx={{ border: `1px solid ${BORDER}` }}>
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell key={column.title} sx={{ ...headSx, width: column.width }}>
            {column.title}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row, index) => (
        <TableRow key={index} className="keep-together">
          {columns.map((column) => (
            <TableCell key={column.title} sx={cellSx}>
              {column.render(row)}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const SeverityTag = ({ severity }) => {
  const config = SEVERITIES.find((s) => s.key === severity);
  return (
    <Box component="span" sx={{ px: 0.8, py: 0.2, borderRadius: 1, bgcolor: config?.bg, color: config?.color, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
      {config?.label}
    </Box>
  );
};

const Identifier = ({ label, value, ltr }) => (
  <Box sx={{ display: "flex", gap: 1, minWidth: 0 }}>
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, fontWeight: 600, flexShrink: 0, width: 92 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13.5, color: TEXT_PRIMARY, fontWeight: 600, minWidth: 0, wordBreak: "break-word" }}>
      {ltr ? <bdi dir="ltr">{value || "—"}</bdi> : value || "—"}
    </Typography>
  </Box>
);

// All the counts of the check in one line (spec 3.6: by severity and status)
const CountsRow = ({ check }) => {
  const severity = check.comparison?.summary || {};
  const status = check.correctionsControl?.state === "done" ? check.correctionsControl.summary : null;
  const auto = check.autoChecks?.summary;
  const parts = [
    `${severity.total || 0} הבדלים בין הגרסאות (${SEVERITIES.map(({ key, label }) => `${severity[key] || 0} ${label}`).join(" · ")})`,
    status &&
      `תיקונים: ${Object.entries(CORRECTION_STATUSES)
        .filter(([key]) => status[key])
        .map(([key, config]) => `${status[key]} ${config.label}`)
        .join(" · ")}`,
    auto && `${auto.total} ממצאים בבדיקות האוטומטיות`,
  ].filter(Boolean);
  return (
    <Typography className="keep-together" sx={{ mt: 1.5, fontSize: 14, color: TEXT_PRIMARY, fontWeight: 600, lineHeight: 1.8 }}>
      {parts.join("  |  ")}
    </Typography>
  );
};

/**
 * The internal report of a check (spec 3.6), in the order the spec lists
 * its parts. Shown in the portal and downloadable as PDF (the rendered
 * report) or Excel (the archived copy).
 */
const PackagingCheckReportPage = () => {
  const { checkId } = useParams();
  const navigate = useNavigate();
  const api = usePackagingCheckApi();
  const { data: check, isLoading, error } = useQuery({
    queryKey: ["packagingCheck", checkId],
    queryFn: () => api.getCheck(checkId),
    refetchInterval: (query) => (query.state.data?.status === "controlling" ? 3000 : false),
  });
  const excel = useReportDownload(check);
  const reportRef = useRef(null);
  const [pdf, setPdf] = useState({ loading: false, error: null });

  const downloadPdf = async () => {
    setPdf({ loading: true, error: null });
    try {
      await downloadElementAsPdf(reportRef.current, reportFileName(check).replace(/\.xlsx$/, ".pdf"));
      setPdf({ loading: false, error: null });
    } catch {
      setPdf({ loading: false, error: "הפקת ה-PDF נכשלה — נסו שוב" });
    }
  };

  const findingsById = useMemo(
    () => new Map((check?.comparison?.findings || []).map((finding) => [finding.id, finding])),
    [check],
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3, pt: 11, maxWidth: 960, mx: "auto" }}>
        <Skeleton variant="rounded" height={480} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }
  if (error || !check) {
    return (
      <Box sx={{ p: 3, pt: 11, maxWidth: 960, mx: "auto" }}>
        <Alert severity="error">{getApiErrorMessage(error, "לא ניתן לטעון את הדוח")}</Alert>
      </Box>
    );
  }

  const control = check.correctionsControl;
  const corrections = (check.corrections || [])
    .map((correction, index) => ({
      index,
      correction,
      result: control?.results?.find((r) => r.correctionIndex === index),
    }))
    .sort((p, q) => statusRank(p.result) - statusRank(q.result) || p.index - q.index);
  // Only a checked corrections list can tell which changes were not requested
  const unrequested =
    control?.state === "done"
      ? (control.unrequestedFindingIds || []).map((id) => findingsById.get(id)).filter(Boolean)
      : [];
  // INCI is one of the sections here, shown as a table of the changed ingredients
  const changedSections = (check.comparison?.sections || []).filter(
    (s) => s.findingIds.length && s.type !== "technical",
  );
  const hasShortenedCorrections = corrections.some(({ correction }) => correction.text.length > MAX_CORRECTION_LENGTH);
  const pending = check.status === "controlling";
  const hasCorrections = corrections.length > 0;
  // Parts are numbered in the order they are shown
  let partNumber = 0;
  const nextPart = () => {
    partNumber += 1;
    return partNumber;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 10, sm: 11 }, maxWidth: 1000, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Button startIcon={<ArrowForwardIcon />} onClick={() => navigate(`/packaging-check/${checkId}`)} sx={{ color: TEXT_SECONDARY, fontWeight: 600 }}>
          חזרה לתוצאות
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<DownloadRoundedIcon />}
          onClick={excel.download}
          disabled={excel.loading || pending}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          הורדה כ-Excel
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<PictureAsPdfRoundedIcon />}
          onClick={downloadPdf}
          disabled={pdf.loading || pending}
          sx={{ borderRadius: 2, fontWeight: 700, bgcolor: BRAND }}
        >
          {pdf.loading ? "מכין PDF…" : "הורדה כ-PDF"}
        </Button>
      </Box>
      {(excel.error || pdf.error) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {excel.error || pdf.error}
        </Alert>
      )}
      {pending && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          בקרת התיקונים עדיין רצה — הדוח יתעדכן אוטומטית בסיומה.
        </Alert>
      )}

      <Paper ref={reportRef} elevation={0} sx={{ p: { xs: 2.5, md: 5 }, borderRadius: 2, border: `1px solid ${BORDER}` }}>
        {/* Internal marking */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#b91c1c", mb: 2 }}>
          <LockRoundedIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.3 }}>{check.report?.marking}</Typography>
        </Box>

        {/* Identifiers */}
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: TEXT_PRIMARY }}>דוח בדיקת גרפיקה — ד״ר עור</Typography>
        <Typography sx={{ fontSize: 15, color: TEXT_SECONDARY, mb: 2 }}>{[checkTitle(check), check.title && check.product?.name].filter(Boolean).join(" · ")}</Typography>
        <Box className="keep-together" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1, rowGap: 0.75, mb: 2.5 }}>
          <Identifier label="גרסה קודמת" value={check.files?.previous?.originalName} ltr />
          <Identifier label='מק"ט' value={check.product?.sku} ltr />
          <Identifier label="גרסה מעודכנת" value={check.files?.updated?.originalName} ltr />
          <Identifier label="ברקוד" value={check.product?.barcode} ltr />
          <Identifier label="תאריך הבדיקה" value={formatDateTime(check.createdAt)} />
          <Identifier label="נבדק על ידי" value={check.createdBy?.name || check.createdBy?.email} />
        </Box>

        {/* Conclusion line */}
        <Box className="keep-together">
          <ConclusionBanner conclusion={check.report?.conclusion} />
        </Box>

        {/* Counts by severity and status */}
        <CountsRow check={check} />

        {/* Without a corrections list (optional, spec 3.1) there is nothing to
            report in these two parts — the limitations statement says so */}
        {hasCorrections && (
          <>
          <ReportSection number={nextPart()} title="תיקונים נדרשים">
            {corrections.length === 0 ? (
              <Empty>לא הוזנה רשימת תיקונים לבדיקה זו.</Empty>
            ) : (
              <SimpleTable
                columns={[
                  { title: "#", width: 32, render: ({ index }) => index + 1 },
                  {
                    title: "תיקון נדרש",
                    width: "34%",
                    render: ({ correction }) => (
                      <Typography dir="auto" sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "pre-wrap" }}>
                        {shorten(correction.text)}
                      </Typography>
                    ),
                  },
                  { title: "סטטוס", width: 110, render: ({ result }) => (result ? <StatusPill status={result.status} /> : "—") },
                  {
                    title: "הסבר וציטוט מהגרסה המעודכנת",
                    render: ({ result }) => (
                      <>
                        <Typography sx={{ fontSize: 13 }}>{result?.explanation || "—"}</Typography>
                        {result?.evidence && (
                          <Typography dir="auto" sx={{ fontSize: 12.5, color: TEXT_SECONDARY, mt: 0.5, fontStyle: "italic" }}>
                            ״{result.evidence}״
                          </Typography>
                        )}
                      </>
                    ),
                  },
                ]}
                rows={corrections}
              />
            )}
            {hasShortenedCorrections && (
              <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 0.75 }}>
                תיקונים ארוכים מוצגים בקיצור — הנוסח המלא בקובץ ה-Excel.
              </Typography>
            )}
          </ReportSection>

          <ReportSection number={nextPart()} title="שינויים שלא נדרשו">
            {unrequested.length === 0 ? (
              <Empty>
                {control?.state === "done"
                  ? "לא נמצאו שינויים שלא נדרשו."
                  : control?.state === "skipped"
                    ? "לא הוזנה רשימת תיקונים — לא ניתן לקבוע אילו שינויים לא נדרשו. כל ההבדלים מופיעים בחלק 4."
                    : "טרם נקבע — בקרת התיקונים לא הושלמה."}
              </Empty>
            ) : (
              <SimpleTable
                columns={[
                  { title: "סעיף", width: 130, render: (f) => f.sectionLabel },
                  { title: "שינוי", width: 90, render: (f) => CHANGE_LABELS[f.type] || f.type },
                  { title: "נוסח לפני", render: (f) => <bdi>{f.before || "—"}</bdi> },
                  { title: "נוסח אחרי", render: (f) => <bdi>{f.after || "—"}</bdi> },
                  { title: "חומרה", width: 70, render: (f) => <SeverityTag severity={f.severity} /> },
                ]}
                rows={unrequested}
              />
            )}
          </ReportSection>
          </>
        )}

        <ReportSection number={nextPart()} title="בדיקות אוטומטיות">
          {!check.autoChecks?.issues?.length ? (
            <Empty>כל הבדיקות האוטומטיות עברו ללא ממצאים.</Empty>
          ) : (
            <SimpleTable
              columns={[
                { title: "בדיקה", width: 150, render: (i) => CHECK_LABELS[i.check] || i.check },
                { title: "סעיף", width: 120, render: (i) => i.sectionLabel || "—" },
                { title: "ממצא", render: (i) => i.message },
                { title: "חומרה", width: 70, render: (i) => <SeverityTag severity={i.severity} /> },
              ]}
              rows={check.autoChecks.issues}
            />
          )}
        </ReportSection>

        <ReportSection number={nextPart()} title="השוואה זו מול זו לפי סעיף">
          {changedSections.length === 0 ? (
            <Empty>לא נמצאו הבדלים בין הגרסאות.</Empty>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {changedSections.map((section) => (
                <Box key={`${section.previousId}-${section.updatedId}`} className="keep-together">
                  <SectionComparison section={section} findingsById={findingsById} visibleSeverities={ALL_SEVERITIES} condensed />
                </Box>
              ))}
            </Box>
          )}
        </ReportSection>

        {/* The limitations statement (spec 3.7), as one short line */}
        <Typography sx={{ mt: 4, fontSize: 12.5, color: TEXT_SECONDARY }}>
          מה לא נבדק: עיצוב (צבע, גופן, מיקום), טקסט שהומר לעקומות ופרשנות רגולטורית. הדוח אינו מהווה אישור לקובץ הדפוס.
        </Typography>

        <Typography sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${BORDER}`, fontSize: 11.5, color: "#94a3b8" }}>
          {check.report?.marking} · מזהה בדיקה {check._id} · הופק {formatDateTime(new Date())}
        </Typography>
      </Paper>
    </Box>
  );
};

export default PackagingCheckReportPage;
